import React from "react";
import { useFela } from "react-fela";

import { Input } from "../../components/Input";
import { useTheme } from "../../hooks/useTheme";
import { ISignupForm } from "./types";
import { IWithCustomStyle } from "../../hooks/useTheme/types";
import { betaSignUp } from "../../services/betaSignUpService";
import { useIsMounted } from "../../hooks/useIsMounted";
import { validateEmail } from "../../utils/validateEmail";
import { Icon, StyledIconWrapper } from "../../components/Icon";

export const SignupForm: React.FC<ISignupForm & IWithCustomStyle> = ({
  title,
  subtitle,
  submitLabel,
  placeholder,
  betaType,
  customStyle = {},
}) => {
  const { css } = useFela();
  const { colors, bp, fonts } = useTheme();
  const isMounted = useIsMounted();

  const [email, setEmail] = React.useState("");
  const [isEmailInvalid, setIsEmailInvalid] = React.useState(false);
  const [, subscribeToNewsletter] = React.useState("IDLE");

  const isPluginForm = betaType === "PLUGIN";

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    setEmail(event.target.value);
    setIsEmailInvalid(false);
  };

  const handleFormSubmit: React.FormEventHandler = event => {
    event.preventDefault();
    const isEmailCorrect = validateEmail(email);
    const betaId = betaType?.toLowerCase();

    if (!isEmailCorrect) {
      setIsEmailInvalid(true);
      return;
    }

    betaSignUp(email, betaId || "")
      .then(() => {
        if (isMounted) {
          setEmail("");
          subscribeToNewsletter("SUCCESS");
        }
      })
      .catch(() => {
        if (isMounted) {
          subscribeToNewsletter("ERROR");
        }
      });
  };

  return (
    <div
      className={css(
        {
          fontSize: 14,
          letterSpacing: 0.44,
        },
        customStyle,
      )}
    >
      {isPluginForm && (
        <span
          className={css({
            position: "absolute",
            top: -10,
            left: "calc(50% - 10px)",
            width: 0,
            height: 0,
            borderStyle: "solid",
            borderWidth: "0 10px 10px 10px",
            borderColor: `transparent transparent ${colors.GRADIENT_BLUE_DARK} transparent`,
          })}
        />
      )}

      {title && (
        <h4
          className={css({
            color: colors.DARK_GREY,
            margin: "0 0 16px",
            fontWeight: "normal",
          })}
        >
          {title}
        </h4>
      )}

      {subtitle && (
        <p
          className={css({
            color: colors.WHITE,
            fontSize: 14,
            marginBottom: 16,
          })}
        >
          {subtitle}
        </p>
      )}

      <form
        onSubmit={handleFormSubmit}
        className={css({
          display: "flex",
          marginBottom: !isPluginForm ? 8 : undefined,
          flexDirection: "column",
          [bp.FROM_TABLET]: {
            flexDirection: "row",
            justifyContent: "center",
          },
        })}
      >
        <Input
          required
          type="email"
          value={email}
          onChange={handleInputChange}
          placeholder={placeholder || "Enter your email address"}
          customStyle={{
            border: `1px solid ${colors.STARDUST}`,
            padding: "8px 16px",
            borderRadius: 4,
            fontSize: "14px",
            color: colors.LIGHT_GRAY,
            fontFamily: fonts.PRIMARY_FONT,
            flexGrow: 2,
            [bp.FROM_TABLET]: {
              marginTop: 0,
              maxWidth: 205,
              marginRight: 10,
            },
          }}
        />

        <input
          type="submit"
          value={submitLabel || "Submit"}
          className={css({
            borderRadius: 4,
            border: "none",
            padding: "8px 30px",
            fontSize: 14,
            fontWeight: "bold",
            color: colors.WHITE,
            backgroundColor: colors.ORANGE,
            fontFamily: fonts.PRIMARY_FONT,
            marginTop: 16,
            appearance: "none",
            cursor: "pointer",
            textTransform: "uppercase",
            [bp.FROM_TABLET]: {
              marginTop: 0,
            },
          })}
        />
      </form>

      {isEmailInvalid && (
        <div
          className={css({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: isPluginForm ? 10 : 15,
          })}
        >
          <StyledIconWrapper width={20} height={20}>
            <Icon iconType="ERROR-ICON" />
          </StyledIconWrapper>

          <p
            className={css({
              fontSize: 14,
              color: colors.RED,
              fontWeight: "bold",
              letterSpacing: 0.5,
              marginLeft: 10,
            })}
          >
            Please enter a valid email address
          </p>
        </div>
      )}
    </div>
  );
};
